import React, { useState } from 'react';
import { Search, X, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FilterSectionProps {
  categories: string[];
  selectedCategory: string;
  searchTerm: string;
  onCategoryChange: (category: string) => void;
  onSearchChange: (term: string) => void;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  categories,
  selectedCategory,
  searchTerm,
  onCategoryChange,
  onSearchChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="group relative mb-6 max-w-2xl mx-auto">
      <div className={`bg-white/95 backdrop-blur-lg rounded-2xl p-1.5 shadow-2xl shadow-blue-100/50 transition-all duration-500 ${isExpanded ? 'ring-2 ring-blue-400/20' : ''}`}>
        
        {/* Main Search Row */}
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
              <Search className="w-4 h-4 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Discover assets..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-transparent text-sm border-0 focus:ring-0 placeholder:text-gray-400/80"
            />
            {searchTerm && (
              <button 
                onClick={() => onSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>

          {/* Filter Trigger */}
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className={`p-2.5 rounded-xl flex items-center gap-1.5 transition-all ${
              selectedCategory ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
            }`}>
            <Filter className="w-4 h-4" />
            <span className="text-sm">{selectedCategory || 'Filters'}</span>
          </button>
        </div>

        {/* Animated Category Grid */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ type: 'spring', bounce: 0.2, duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t border-gray-100/50">
                <div className="grid grid-cols-3 gap-2 px-1 pb-1">
                  {categories.map((category) => (
                    <motion.button
                      key={category}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        onCategoryChange(category === selectedCategory ? '' : category);
                        setIsExpanded(false);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-lg flex items-center justify-center transition-all ${
                        category === selectedCategory 
                          ? 'bg-blue-500 text-white shadow-md shadow-blue-200'
                          : 'bg-gray-100 hover:bg-gray-200/80 text-gray-600'
                      }`}
                    >
                      {category}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating Status Indicator */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
        {searchTerm && (
          <motion.span 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full shadow-md">
            Searching: {searchTerm}
          </motion.span>
        )}
        {selectedCategory && (
          <motion.span 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full shadow-md">
            {selectedCategory}
          </motion.span>
        )}
      </div>
    </div>
  );
};